import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import { getMenuList } from "@/lib/menu-list";

interface MenuGroupsState {
  collapsedGroups: Record<number, boolean>;
}

const getDefaultCollapsedGroups = (): Record<number, boolean> => {
  const t = (key: string) => key;
  const menuList = getMenuList({ t });
  return menuList.reduce((acc, _, index) => {
    acc[index] = true;
    return acc;
  }, {} as Record<number, boolean>);
};

const getInitialMenuGroupsState = (): MenuGroupsState => {
  const savedState = Cookies.get("menuGroups");
  if (!savedState) {
    return {
      collapsedGroups: getDefaultCollapsedGroups(),
    };
  }

  try {
    const parsedState = JSON.parse(savedState);
    if (typeof parsedState !== "object" || parsedState === null) {
      throw new Error("Invalid state format");
    }

    const validState = Object.entries(parsedState).reduce((acc, [key, value]) => {
      const index = parseInt(key, 10);
      if (!isNaN(index) && typeof value === "boolean") {
        acc[index] = value;
      }
      return acc;
    }, {} as Record<number, boolean>);

    if (Object.keys(validState).length === 0) {
      throw new Error("No valid state entries");
    }

    const defaultState = getDefaultCollapsedGroups();
    const mergedState = { ...defaultState, ...validState };

    return {
      collapsedGroups: mergedState,
    };
  } catch (error: unknown) {
    console.warn("Failed to parse menu groups state:", error);
    Cookies.remove("menuGroups");
    return {
      collapsedGroups: getDefaultCollapsedGroups(),
    };
  }
};

const menuGroupsSlice = createSlice({
  name: "menuGroups",
  initialState: getInitialMenuGroupsState(),
  reducers: {
    toggleGroup: (state, action: PayloadAction<number>) => {
      state.collapsedGroups[action.payload] = !state.collapsedGroups[action.payload];
      Cookies.set("menuGroups", JSON.stringify(state.collapsedGroups));
    },
    setGroupState: (state, action: PayloadAction<{ index: number; isCollapsed: boolean }>) => {
      state.collapsedGroups[action.payload.index] = action.payload.isCollapsed;
      Cookies.set("menuGroups", JSON.stringify(state.collapsedGroups));
    },
  },
});

export const { toggleGroup, setGroupState } = menuGroupsSlice.actions;
export default menuGroupsSlice.reducer; 